import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Group,
  Modal,
  TextInput,
  Stack,
  Text,
  Divider,
  Title,
} from "@mantine/core";
import { toast } from "sonner";
import { IconLink, IconLock, IconInfoCircle } from "@tabler/icons-react";

import { useLinkAccountingSoftwareMutation } from "../api/queries/organizationQueries";
import { IntegrationConnectButton } from "./integrations/IntegrationButtons";

interface StripeFormData {
  apiKey: string;
}

const INITIAL_FORM_DATA: StripeFormData = {
  apiKey: "",
};

export function StripeConnection() {
  const { mutate: linkAccountingSoftware, isPending: isLinkingStripe } =
    useLinkAccountingSoftwareMutation();

  const [stripeModalOpened, setStripeModalOpened] = useState(false);
  const [formData, setFormData] = useState<StripeFormData>(INITIAL_FORM_DATA);

  const isFormValid = useMemo(() => formData.apiKey.trim() !== "", [formData]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseModal = useCallback(() => {
    setStripeModalOpened(false);
    resetForm();
  }, [resetForm]);

  const handleOpenModal = useCallback(() => {
    setStripeModalOpened(true);
  }, []);

  const handleFormChange = useCallback(
    (field: keyof StripeFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      },
    [],
  );

  const handleStripeConnect = useCallback(() => {
    if (!isFormValid) {
      toast.error("Please enter your Stripe API key");
      return;
    }

    const apiKey = formData.apiKey.trim();

    // Optional: Validate format (starts with sk_live_ or sk_test_)
    if (!apiKey.startsWith("sk_live_") && !apiKey.startsWith("sk_test_")) {
      toast.error(
        "Invalid API key format. Must start with sk_live_ or sk_test_",
      );
      return;
    }

    // Save the API key (will be encrypted on backend)
    linkAccountingSoftware(
      {
        name: "Stripe",
        api_key: apiKey,
      },
      {
        onSuccess: () => {
          toast.success("Successfully connected to Stripe");
          // Clear form immediately after success
          resetForm();
          handleCloseModal();
        },
        onError: (err: any) => {
          // Clear form even on error to remove sensitive data from memory
          resetForm();
          const detail = err?.response?.data?.detail;
          const errorMessage =
            (typeof detail === "object" && detail?.message
              ? detail.message
              : typeof detail === "string"
                ? detail
                : null) ||
            err?.message ||
            "Failed to save API key";
          toast.error("Failed to connect to Stripe", {
            description: errorMessage,
          });
        },
      },
    );
  }, [
    formData,
    isFormValid,
    linkAccountingSoftware,
    handleCloseModal,
    resetForm,
  ]);

  return (
    <>
      <IntegrationConnectButton onClick={handleOpenModal} />

      <Modal
        opened={stripeModalOpened}
        onClose={handleCloseModal}
        title={
          <Group gap="xs">
            <IconLink size={20} />
            <Title order={4}>Connect to Stripe</Title>
          </Group>
        }
        centered
        closeOnClickOutside={!isLinkingStripe}
        closeOnEscape={!isLinkingStripe}
        size="lg"
        radius="md"
        padding="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
            Enter your Stripe API key to connect your account. Your API key is
            encrypted and stored securely. This will allow Datamint to sync
            invoices and data from your Stripe account.
          </Text>

          <Divider />

          <Stack gap="md">
            <TextInput
              label="Stripe API Key"
              placeholder="sk_live_... or sk_test_..."
              value={formData.apiKey}
              onChange={handleFormChange("apiKey")}
              type="password"
              required
              disabled={isLinkingStripe}
              description="Enter your Stripe API key (starts with sk_live_ for live mode or sk_test_ for test mode)"
              leftSection={<IconLock size={18} />}
              radius="md"
              size="md"
              error={
                formData.apiKey &&
                formData.apiKey.trim() !== "" &&
                !formData.apiKey.trim().startsWith("sk_live_") &&
                !formData.apiKey.trim().startsWith("sk_test_")
                  ? "API key must start with sk_live_ or sk_test_"
                  : null
              }
            />
          </Stack>

          <Group
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
            gap="xs"
          >
            <IconInfoCircle size={18} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Your API key is encrypted and stored securely. It will only be
              used to access your Stripe account.
            </Text>
          </Group>

          <Divider />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isLinkingStripe}
              radius="md"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleStripeConnect}
              loading={isLinkingStripe}
              disabled={!isFormValid || isLinkingStripe}
              leftSection={!isLinkingStripe && <IconLink size={16} />}
              variant="gradient"
              gradient={{ from: "purple", to: "pink", deg: 90 }}
              radius="md"
              size="md"
            >
              {isLinkingStripe ? "Connecting..." : "Connect"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
